import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { createIssue } from '../store/slices/issueSlice'
import { fetchProjectStatuses } from '../store/slices/boardStatusSlice'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { IssuePriority } from '@/types'
import { 
  CheckIcon, 
  CalendarIcon,
  UserIcon,
  ChevronDownIcon 
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'

interface QuickCreateIssueModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: string
  statusId: string
  statusName: string
}

const QuickCreateIssueModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  statusId, 
  statusName 
}: QuickCreateIssueModalProps) => {
  const [title, setTitle] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useAppDispatch()
  const { currentProject } = useAppSelector(state => state.project)
  const { statuses } = useAppSelector(state => state.boardStatus)
  
  // 專案權限檢查
  const { hasPermission } = usePermissions(projectId)
  const canCreateIssue = hasPermission(Permission.CREATE_ISSUE)
  const canAssignIssue = hasPermission(Permission.ASSIGN_ISSUE)

  // 確保在模態框打開時獲取專案狀態
  useEffect(() => {
    if (isOpen && projectId && statuses.length === 0) {
      dispatch(fetchProjectStatuses(projectId))
    }
  }, [dispatch, projectId, isOpen, statuses.length])

  // 重置表單
  useEffect(() => {
    if (!isOpen) {
      setTitle('')
      setDueDate('')
      setAssigneeId('')
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !canCreateIssue) return

    setIsSubmitting(true)
    try {
      const issueData: any = {
        title: title.trim(),
        projectId,
        customStatus: statusId,
        priority: IssuePriority.MEDIUM, // 預設中等優先級
      }
      
      if (assigneeId) {
        issueData.assigneeId = assigneeId
      }
      
      if (dueDate) {
        issueData.dueDate = new Date(dueDate).toISOString()
      }
      
      await dispatch(createIssue(issueData)).unwrap()
      onClose()
    } catch (error) {
      console.error('建立議題失敗:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAssigneeName = (userId: string) => {
    const member = currentProject?.members?.find(m => m.user.id === userId)
    return member?.user.name || '未指派'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen p-4 pt-16">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative inline-block w-full max-w-lg p-6 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg mb-16">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 標題輸入 */}
            <div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="需要完成什麼？"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                required
              />
            </div>

            {/* 狀態標籤 */}
            <div className="text-sm text-gray-500">
              將建立在 <span className="font-medium text-gray-700">{statusName}</span> 狀態中
            </div>

            {/* 底部操作區 */}
            <div className="flex items-center justify-between pt-2">
              {/* 左側：日期和負責人選擇 */}
              <div className="flex items-center space-x-3">
                {/* 到期日選擇 */}
                <div className="relative">
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <CalendarIcon className="absolute left-2 top-2.5 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {/* 負責人選擇 */}
                {canAssignIssue && currentProject?.members && (
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500">
                      <UserIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">
                        {assigneeId ? getAssigneeName(assigneeId) : '指派給'}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-[60]">
                        <div className="py-1 max-h-48 overflow-y-auto">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => setAssigneeId('')}
                                className={`${
                                  active ? 'bg-gray-100' : ''
                                } w-full text-left px-4 py-2 text-sm text-gray-700`}
                              >
                                未指派
                              </button>
                            )}
                          </Menu.Item>
                          {currentProject.members.map((member) => (
                            <Menu.Item key={member.user.id}>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => setAssigneeId(member.user.id)}
                                  className={`${
                                    active ? 'bg-gray-100' : ''
                                  } w-full text-left px-4 py-2 text-sm text-gray-700 flex items-center space-x-2`}
                                >
                                  {member.user.avatar ? (
                                    <img
                                      src={member.user.avatar}
                                      alt={member.user.name}
                                      className="w-6 h-6 rounded-full"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                                      <span className="text-xs text-gray-600">
                                        {member.user.name.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  )}
                                  <span>{member.user.name}</span>
                                  {assigneeId === member.user.id && (
                                    <CheckIcon className="w-4 h-4 text-blue-600 ml-auto" />
                                  )}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              </div>

              {/* 右側：提交和取消按鈕 */}
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                  disabled={isSubmitting}
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={!title.trim() || isSubmitting}
                  className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckIcon className="w-4 h-4" />
                  <span>{isSubmitting ? '建立中...' : '建立'}</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default QuickCreateIssueModal
